import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, CalendarBlank, Trash, Images, VideoCamera, YoutubeLogo, GraduationCap } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStudent } from "@/context/StudentContext";
import { PageHeader } from "@/components/PageHeader";
import { LiteYouTube } from "@/components/LiteYouTube";
import { wibNow } from "@/lib/time";
import { toast } from "sonner";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};
const TODAY = wibNow().toISOString().slice(0, 10);
const TK_CLASSES = ["TK B", "TK B-1", "TK B-2", "TK B-3", "TK A", "TK A-1", "TK A-2", "TK A-3", "KB"];
const SD_CLASSES = ["Kelas 1", "Kelas 1A", "Kelas 1B", "Kelas 2", "Kelas 2A", "Kelas 2B", "Kelas 3", "Kelas 3A", "Kelas 3B", "Kelas 4", "Kelas 4A", "Kelas 4B", "Kelas 5", "Kelas 5A", "Kelas 5B", "Kelas 6", "Kelas 6A", "Kelas 6B"];
const groupOf = (k) => (!k ? "" : k.startsWith("Kelas ") ? "Kelas " + k.split(" ")[1][0] : k.startsWith("TK B") ? "TK B" : k.startsWith("TK A") ? "TK A" : k.startsWith("KB") ? "KB" : "");
const inClass = (itemKelas, sel) => {
  if (sel === "Semua" || !itemKelas || itemKelas === "Semua") return true;
  if (sel.includes("-")) return itemKelas === sel;
  return groupOf(itemKelas) === sel;
};

export default function Galeri() {
  const { user } = useAuth();
  const { active } = useStudent();
  const isAdmin = user?.role === "admin";
  const CLASS_GROUPS = user?.school === "sdqu" ? SD_CLASSES : TK_CLASSES;
  const childGroup = groupOf(active?.kelas);
  const [view, setView] = useState("foto");
  const [cls, setCls] = useState("Semua");
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  // learning media (Video Pembelajaran)
  const [lm, setLm] = useState([]);
  const [lmVOpen, setLmVOpen] = useState(false);
  const [lmVTitle, setLmVTitle] = useState("");
  const [lmVUrl, setLmVUrl] = useState("");
  const [lmVSaving, setLmVSaving] = useState(false);
  const [lmPOpen, setLmPOpen] = useState(false);
  const [lmPTitle, setLmPTitle] = useState("");
  const [lmPFile, setLmPFile] = useState(null);
  const [lmUploading, setLmUploading] = useState(false);

  const load = () => {
    api.get("/gallery").then((r) => setItems(r.data));
    api.get("/gallery/videos").then((r) => setVideos(r.data));
    api.get("/learning-media").then((r) => setLm(r.data)).catch(() => {});
  };
  useEffect(load, []);
  // default to the child's class for parents, the guru's class for teachers
  useEffect(() => {
    if (user?.role === "orangtua" && active?.kelas) setCls(active.kelas);
    else if (user?.role === "guru" && user?.kelas) setCls(user.kelas);
  }, [user, childGroup, active]);

  // photo add
  const [pOpen, setPOpen] = useState(false);
  const [pDate, setPDate] = useState(TODAY);
  const [pTitle, setPTitle] = useState("");
  const [pKelas, setPKelas] = useState("TK B");
  const [pFiles, setPFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    const d = user?.school === "sdqu" ? "Kelas 1" : "TK B";
    setPKelas(d); setVKelas(d);
  }, [user]);

  // video add
  const [vOpen, setVOpen] = useState(false);
  const [vDate, setVDate] = useState(TODAY);
  const [vTitle, setVTitle] = useState("");
  const [vKelas, setVKelas] = useState("TK B");
  const [vUrl, setVUrl] = useState("");
  const [vSaving, setVSaving] = useState(false);

  const fItems = items.filter((it) => inClass(it.kelas, cls));
  const fVideos = videos.filter((v) => inClass(v.kelas, cls));

  const groups = [];
  fItems.forEach((it) => {
    const date = it.event_date || (it.created_at || "").slice(0, 10);
    const title = it.title || it.caption || "Kegiatan";
    const key = `${date}|${title}`;
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, date, title, imgs: [] }; groups.push(g); }
    g.imgs.push(it);
  });

  const savePhotos = async () => {
    if (!pTitle.trim()) { toast.error("Isi judul/tema"); return; }
    if (!pFiles.length) { toast.error("Pilih minimal 1 foto"); return; }
    setUploading(true);
    try {
      for (const f of pFiles) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/gallery?title=${encodeURIComponent(pTitle)}&event_date=${pDate}&kelas=${encodeURIComponent(pKelas)}&caption=${encodeURIComponent(pTitle)}`, fd);
      }
      toast.success("Foto galeri ditambahkan");
      setPOpen(false); setPFiles([]); setPTitle(""); setPDate(TODAY);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Yakin mau dihapus?")) return;
    try { await api.delete(`/gallery/${id}`); setItems((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveVideo = async () => {
    if (!vTitle.trim()) { toast.error("Isi judul video"); return; }
    if (!vUrl.trim()) { toast.error("Tempel link YouTube"); return; }
    setVSaving(true);
    try {
      await api.post("/gallery/videos", { title: vTitle, video_date: vDate, youtube_url: vUrl, kelas: vKelas });
      toast.success("Video ditambahkan");
      setVOpen(false); setVTitle(""); setVUrl(""); setVDate(TODAY);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setVSaving(false); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Yakin mau dihapus?")) return;
    try { await api.delete(`/gallery/videos/${id}`); setVideos((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveLmVideo = async () => {
    if (!lmVTitle.trim()) { toast.error("Isi judul video"); return; }
    if (!lmVUrl.trim()) { toast.error("Tempel link YouTube"); return; }
    setLmVSaving(true);
    try {
      await api.post("/learning-media/video", { title: lmVTitle, youtube_url: lmVUrl });
      toast.success("Video pembelajaran ditambahkan");
      setLmVOpen(false); setLmVTitle(""); setLmVUrl(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLmVSaving(false); }
  };

  const saveLmPhoto = async () => {
    if (!lmPFile) { toast.error("Pilih foto"); return; }
    setLmUploading(true);
    try {
      const fd = new FormData(); fd.append("file", lmPFile);
      await api.post(`/learning-media/photo?title=${encodeURIComponent(lmPTitle || "Foto pembelajaran")}`, fd);
      toast.success("Foto pembelajaran ditambahkan");
      setLmPOpen(false); setLmPFile(null); setLmPTitle(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLmUploading(false); }
  };

  const deleteLm = async (id) => {
    if (!window.confirm("Yakin mau dihapus?")) return;
    try { await api.delete(`/learning-media/${id}`); setLm((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Galeri Kegiatan" />
      <div className="px-5 pt-4 pb-8">
        {/* Class folders */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
          {["Semua", ...CLASS_GROUPS].map((c) => (
            <button key={c} data-testid={`galeri-class-${c}`} onClick={() => setCls(c)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${cls === c ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
              {c === "Semua" ? "Semua" : `📁 ${c}`}
            </button>
          ))}
        </div>

        {/* Foto / Video / Pembelajaran toggle */}
        <div className="flex gap-1.5 bg-white border border-stone-200/60 rounded-2xl p-1 shadow-sm mb-4">
          <button data-testid="galeri-tab-foto" onClick={() => setView("foto")}
            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${view === "foto" ? "bg-primary text-white shadow" : "text-stone-500"}`}>
            <Images size={16} weight={view === "foto" ? "fill" : "regular"} /> Foto
          </button>
          <button data-testid="galeri-tab-video" onClick={() => setView("video")}
            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${view === "video" ? "bg-primary text-white shadow" : "text-stone-500"}`}>
            <VideoCamera size={16} weight={view === "video" ? "fill" : "regular"} /> Video
          </button>
          <button data-testid="galeri-tab-belajar" onClick={() => setView("belajar")}
            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${view === "belajar" ? "bg-primary text-white shadow" : "text-stone-500"}`}>
            <GraduationCap size={16} weight={view === "belajar" ? "fill" : "regular"} /> Pembelajaran
          </button>
        </div>

        {view === "foto" ? (
          <>
            <h3 className="font-heading font-bold text-stone-800 text-base mb-3" data-testid="galeri-foto-title">Foto Dokumentasi Kegiatan Sekolah</h3>
            {isAdmin && (
              <button data-testid="galeri-add-foto-btn" onClick={() => setPOpen(true)}
                className="w-full mb-4 border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                <Plus size={18} weight="bold" /> Tambah Foto Galeri
              </button>
            )}
            {groups.map((g, gi) => (
              <div key={g.key} data-testid={`galeri-group-${gi}`} className="mb-6">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <CalendarBlank size={18} weight="duotone" className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-stone-800 leading-tight truncate">{g.title}</p>
                    <p className="text-stone-400 text-xs">{fmtDate(g.date)}{g.imgs[0]?.kelas && g.imgs[0].kelas !== "Semua" ? ` • ${g.imgs[0].kelas}` : ""}</p>
                  </div>
                </div>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                  {g.imgs.map((img, i) => (
                    <motion.div key={img.id || i} data-testid={`gallery-img-${gi}-${i}`}
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                      onClick={() => setLightbox(img.url)}
                      className="relative rounded-2xl overflow-hidden w-40 h-40 shrink-0 bg-stone-100 shadow-sm cursor-pointer">
                      <img src={img.thumb || img.url} alt={img.title} loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("bg-primary/10"); }}
                        className="w-full h-full object-cover" />
                      {isAdmin && (
                        <button data-testid={`gallery-del-${img.id}`} onClick={(e) => { e.stopPropagation(); deletePhoto(img.id); }}
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center active:scale-90 transition-transform" aria-label="Hapus foto">
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
            {groups.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada foto.</p>}
          </>
        ) : view === "video" ? (
          <>
            <h3 className="font-heading font-bold text-stone-800 text-base mb-3" data-testid="galeri-video-title">Video Dokumentasi Kegiatan Sekolah</h3>
            {isAdmin && (
              <button data-testid="galeri-add-video-btn" onClick={() => setVOpen(true)}
                className="w-full mb-4 border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                <Plus size={18} weight="bold" /> Tambah Video YouTube
              </button>
            )}
            <div className="space-y-5">
              {fVideos.map((v, i) => (
                <motion.div key={v.id} data-testid={`gallery-video-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
                  <div className="aspect-video bg-black">
                    <LiteYouTube id={v.youtube_id} title={v.title} />
                  </div>
                  <div className="p-3.5 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-heading font-semibold text-stone-800 leading-tight">{v.title}</p>
                      <p className="text-stone-400 text-xs mt-0.5 flex items-center gap-1"><CalendarBlank size={12} weight="fill" /> {fmtDate(v.video_date)}{v.kelas && v.kelas !== "Semua" ? ` • ${groupOf(v.kelas)}` : ""}</p>
                    </div>
                    {isAdmin && (
                      <button data-testid={`video-del-${v.id}`} onClick={() => deleteVideo(v.id)}
                        className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-destructive active:scale-90 transition-transform shrink-0" aria-label="Hapus video">
                        <Trash size={15} weight="bold" />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
              {fVideos.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada video kegiatan.</p>}
            </div>
          </>
        ) : (
          <>
            <h3 className="font-heading font-bold text-stone-800 text-base mb-3" data-testid="galeri-belajar-title">Video Pembelajaran</h3>
            <p className="text-stone-400 text-xs mb-3">Video &amp; foto pembelajaran untuk semua kelas (satu sekolah). Geser ke kiri/kanan.</p>
            {isAdmin && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button data-testid="lm-add-video-btn" onClick={() => setLmVOpen(true)}
                  className="border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform text-sm">
                  <YoutubeLogo size={18} weight="fill" className="text-red-500" /> Tambah Video
                </button>
                <button data-testid="lm-add-photo-btn" onClick={() => setLmPOpen(true)}
                  className="border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform text-sm">
                  <Images size={18} weight="bold" /> Tambah Foto
                </button>
              </div>
            )}
            <div className="space-y-4">
              {lm.map((it, i) => (
                <motion.div key={it.id} data-testid={`lm-item-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
                  <div className="aspect-video bg-black">
                    {it.type === "video"
                      ? <LiteYouTube id={it.youtube_id} title={it.title} />
                      : <img src={it.thumb || it.url} alt={it.title} loading="lazy" className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(it.url)} />}
                  </div>
                  <div className="p-3.5 flex items-start justify-between gap-2">
                    <p className="font-heading font-semibold text-stone-800 leading-tight">{it.title}</p>
                    {isAdmin && (
                      <button data-testid={`lm-del-${it.id}`} onClick={() => deleteLm(it.id)}
                        className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-destructive active:scale-90 transition-transform shrink-0" aria-label="Hapus">
                        <Trash size={15} weight="bold" />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
              {lm.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada video pembelajaran.</p>}
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" data-testid="galeri-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center" aria-label="Tutup"><X size={22} /></button>
          <img src={lightbox} alt="Foto" className="max-w-full max-h-full object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Add photo modal */}
      {pOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setPOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Tambah Foto Galeri</h3>
              <button onClick={() => setPOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Tanggal</p>
            <input data-testid="foto-date-input" type="date" value={pDate} onChange={(e) => setPDate(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Kelas</p>
            <select data-testid="foto-kelas-input" value={pKelas} onChange={(e) => setPKelas(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary">
              {CLASS_GROUPS.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Semua">Semua Kelas</option>
            </select>
            <p className="text-xs text-stone-500 mb-1">Judul / Tema</p>
            <input data-testid="foto-title-input" type="text" value={pTitle} onChange={(e) => setPTitle(e.target.value)}
              placeholder="Contoh: Manasik Haji" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Foto (bisa lebih dari 1)</p>
            <input data-testid="foto-files-input" type="file" accept="image/png,image/jpeg" multiple onChange={(e) => setPFiles(Array.from(e.target.files || []))}
              className="w-full text-sm mb-2" />
            {pFiles.length > 0 && <p className="text-xs text-primary mb-3">{pFiles.length} foto dipilih</p>}
            <button data-testid="foto-save-btn" onClick={savePhotos} disabled={uploading}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform disabled:opacity-60">
              {uploading ? "Mengunggah..." : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {/* Add video modal */}
      {vOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setVOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800 flex items-center gap-2"><YoutubeLogo size={22} weight="fill" className="text-red-500" /> Tambah Video</h3>
              <button onClick={() => setVOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Tanggal</p>
            <input data-testid="video-date-input" type="date" value={vDate} onChange={(e) => setVDate(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Kelas</p>
            <select data-testid="video-kelas-input" value={vKelas} onChange={(e) => setVKelas(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary">
              {CLASS_GROUPS.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Semua">Semua Kelas</option>
            </select>
            <p className="text-xs text-stone-500 mb-1">Judul Video</p>
            <input data-testid="video-title-input" type="text" value={vTitle} onChange={(e) => setVTitle(e.target.value)}
              placeholder="Contoh: Pentas Seni Akhir Tahun" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Link YouTube</p>
            <input data-testid="video-url-input" type="text" value={vUrl} onChange={(e) => setVUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..." className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="video-save-btn" onClick={saveVideo} disabled={vSaving}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform disabled:opacity-60">
              {vSaving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {/* Add learning video modal */}
      {lmVOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setLmVOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800 flex items-center gap-2"><GraduationCap size={22} weight="fill" className="text-emerald-500" /> Video Pembelajaran</h3>
              <button onClick={() => setLmVOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Judul</p>
            <input data-testid="lm-video-title-input" type="text" value={lmVTitle} onChange={(e) => setLmVTitle(e.target.value)}
              placeholder="Contoh: Adab Naik Kendaraan" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Link YouTube</p>
            <input data-testid="lm-video-url-input" type="text" value={lmVUrl} onChange={(e) => setLmVUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..." className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="lm-video-save-btn" onClick={saveLmVideo} disabled={lmVSaving}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform disabled:opacity-60">
              {lmVSaving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {/* Add learning photo modal */}
      {lmPOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setLmPOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800 flex items-center gap-2"><Images size={22} weight="fill" className="text-emerald-500" /> Foto Pembelajaran</h3>
              <button onClick={() => setLmPOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Judul</p>
            <input data-testid="lm-photo-title-input" type="text" value={lmPTitle} onChange={(e) => setLmPTitle(e.target.value)}
              placeholder="Contoh: Belajar Wudhu" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Foto (JPG/PNG, maks 3MB)</p>
            <input data-testid="lm-photo-file-input" type="file" accept="image/png,image/jpeg" onChange={(e) => setLmPFile(e.target.files?.[0] || null)}
              className="w-full text-sm mb-4" />
            <button data-testid="lm-photo-save-btn" onClick={saveLmPhoto} disabled={lmUploading}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform disabled:opacity-60">
              {lmUploading ? "Mengunggah..." : "Simpan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
